import axios from 'axios';

import type {
  DismissSuggestionBody,
  HolidayDto,
  SuggestionDto,
  SuggestionsListDto,
} from './proactive.dto';

// core-ai, like every /api/ai call: bare axios on VITE_APP_EXTRACT_URL, the
// X-Backend-Env header is stamped by initBackendEnvHeader (src/utils/appEnv.ts).
const base = () => `${import.meta.env.VITE_APP_EXTRACT_URL}/api/ai/proactive`;

/** Suggestions still on the table: open ones and snoozed ones (still clickable on the calendar). */
export const getLiveSuggestions = async (
  vesselId: string,
  userId: string,
): Promise<SuggestionDto[]> => {
  const { data } = await axios.get<SuggestionsListDto>(
    `${base()}/vessels/${vesselId}/suggestions`,
    {
      params: { user_id: userId, status: 'open,snoozed' },
    },
  );
  return data.suggestions;
};

export const markSuggestionSeen = async (id: string, userId: string): Promise<SuggestionDto> => {
  const { data } = await axios.post<SuggestionDto>(`${base()}/suggestions/${id}/seen`, {
    user_id: userId,
  });
  return data;
};

export const acceptSuggestion = async (
  id: string,
  userId: string,
  outcomeRef?: string,
): Promise<SuggestionDto> => {
  const { data } = await axios.post<SuggestionDto>(`${base()}/suggestions/${id}/accept`, {
    user_id: userId,
    outcome_ref: outcomeRef,
  });
  return data;
};

/** Remind at the moment the person picked (must be before the idea expires). */
export const snoozeSuggestion = async (
  id: string,
  userId: string,
  until: Date,
): Promise<SuggestionDto> => {
  const { data } = await axios.post<SuggestionDto>(`${base()}/suggestions/${id}/snooze`, {
    user_id: userId,
    until: until.toISOString(),
  });
  return data;
};

export const dismissSuggestion = async (
  id: string,
  body: DismissSuggestionBody,
): Promise<SuggestionDto> => {
  const { data } = await axios.post<SuggestionDto>(`${base()}/suggestions/${id}/dismiss`, body);
  return data;
};

/** The US holidays of a year — static data, no auth. */
export const getHolidays = async (year: number): Promise<HolidayDto[]> => {
  const { data } = await axios.get<HolidayDto[]>(`${base()}/holidays`, { params: { year } });
  return data;
};

/** An idea for an upcoming holiday on request (a click in the calendar): the existing one, or a new one. */
export const requestHolidayIdea = async (
  vesselId: string,
  holidayKey: string,
  userId: string,
  year: number,
): Promise<SuggestionDto> => {
  const { data } = await axios.post<SuggestionDto>(
    `${base()}/vessels/${vesselId}/holidays/${holidayKey}/idea`,
    { user_id: userId, year },
  );
  return data;
};
